import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const QUICK_TAGS_CUSTOMER = [
  { key: 'on_time', label: 'On time', icon: '⏱️' },
  { key: 'friendly', label: 'Friendly', icon: '😊' },
  { key: 'clean_vehicle', label: 'Clean vehicle', icon: '✨' },
  { key: 'safe_driving', label: 'Safe driving', icon: '🛡️' },
  { key: 'professional', label: 'Professional', icon: '👔' },
];

const QUICK_TAGS_DRIVER = [
  { key: 'on_time', label: 'On time', icon: '⏱️' },
  { key: 'friendly', label: 'Friendly', icon: '😊' },
  { key: 'clear_instructions', label: 'Clear instructions', icon: '📋' },
  { key: 'good_tipper', label: 'Good tipper', icon: '💰' },
  { key: 'professional', label: 'Professional', icon: '👔' },
];

const starLabels = ['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'];

const RatingSubmissionScreen = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams?.get('orderId');
  const reviewerRole = searchParams?.get('reviewerRole') || 'customer';

  const [order, setOrder] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(true);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [selectedTags, setSelectedTags] = useState([]);
  const [reviewText, setReviewText] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const quickTags = reviewerRole === 'driver' ? QUICK_TAGS_DRIVER : QUICK_TAGS_CUSTOMER;

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) { setLoadingOrder(false); return; }
      try {
        const { data, error: err } = await supabase
          ?.from('orders')
          ?.select('id, order_number, pickup_address, dropoff_address, total_amount, created_at, status, driver_profiles(user_profiles(full_name))')
          ?.eq('id', orderId)
          ?.single();
        if (!err && data) setOrder(data);
      } catch (e) {
        console.error('Failed to fetch order:', e);
      } finally {
        setLoadingOrder(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  const handleTagToggle = (key) => {
    setSelectedTags(prev =>
      prev?.includes(key) ? prev?.filter(t => t !== key) : [...prev, key]
    );
  };

  const handlePhotoSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (file?.size > 5 * 1024 * 1024) { setError('Photo must be under 5MB'); return; }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader?.result);
    reader?.readAsDataURL(file);
    setError(null);
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef?.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (rating === 0) { setError('Please select a star rating'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      let photoUrl = null;
      if (photoFile) {
        const ext = photoFile?.name?.split('.')?.pop();
        const fileName = `ratings/${orderId}/${user?.id}_${Date.now()}.${ext}`;
        const { data: uploadData, error: uploadError } = await supabase?.storage
          ?.from('order-photos')
          ?.upload(fileName, photoFile, { upsert: true });
        if (!uploadError && uploadData) {
          const { data: urlData } = supabase?.storage?.from('order-photos')?.getPublicUrl(fileName);
          photoUrl = urlData?.publicUrl || null;
        }
      }

      const tagText = selectedTags?.length > 0 ? `[${selectedTags?.join(', ')}]` : '';
      const fullReview = reviewText?.trim()
        ? tagText ? `${tagText} ${reviewText?.trim()}` : reviewText?.trim()
        : tagText || null;

      const { error: insertError } = await supabase
        ?.from('order_ratings')
        ?.insert({
          order_id: orderId,
          reviewer_id: user?.id,
          reviewer_role: reviewerRole,
          rating,
          review_text: fullReview,
          photo_url: photoUrl,
        });

      if (insertError) throw insertError;
      setSubmitted(true);

      setTimeout(() => {
        if (reviewerRole === 'driver') {
          navigate('/driver-dashboard-screen');
        } else {
          navigate('/customer-booking-screen');
        }
      }, 2500);
    } catch (err) {
      console.error('Rating submit error:', err);
      setError(err?.message || 'Failed to submit rating. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr)?.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const driverName = order?.driver_profiles?.user_profiles?.full_name || null;

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center px-4">
        <div className="text-center max-w-sm w-full">
          <div className="relative mx-auto mb-6 w-28 h-28">
            <div className="absolute inset-0 rounded-full bg-[#ffd700]/20 animate-ping" />
            <div className="absolute inset-2 rounded-full bg-[#ffd700]/30 animate-pulse" />
            <div className="relative w-28 h-28 rounded-full bg-[#ffd700] flex items-center justify-center shadow-2xl">
              <svg viewBox="0 0 24 24" fill="none" stroke="#0a1628" strokeWidth="3" className="w-14 h-14">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
          </div>
          <h2 className="text-white font-black text-3xl mb-3">Thank You!</h2>
          <p className="text-white/60 text-base mb-3">Your rating has been submitted.</p>
          <div className="flex items-center justify-center gap-1 mb-4">
            {Array.from({ length: 5 })?.map((_, i) => (
              <svg key={i} viewBox="0 0 24 24" className="w-7 h-7" fill={i < rating ? '#ffd700' : '#ffffff20'}>
                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
              </svg>
            ))}
          </div>
          <p className="text-white/30 text-xs">Redirecting you back...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1628]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div className="sticky top-0 z-20 bg-[#0a1628]/95 backdrop-blur-md border-b border-[#ffd700]/10">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center hover:bg-white/15 transition-colors flex-shrink-0"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" className="w-4 h-4">
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-md bg-[#ffd700] flex items-center justify-center flex-shrink-0">
                <span className="text-[#0a1628] font-black text-xs">OG</span>
              </div>
              <span className="text-white font-bold text-sm">Rate Your Experience</span>
            </div>
            <p className="text-white/40 text-xs">
              {reviewerRole === 'driver' ? 'Rate this customer' : 'Rate your delivery'}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 pb-10 pt-4 space-y-4">
        {/* Trip Summary Card */}
        {loadingOrder ? (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-4 animate-pulse h-28" />
        ) : (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
            <p className="text-white/40 text-xs font-semibold uppercase tracking-wider mb-3">Trip Summary</p>
            <div className="space-y-2.5">
              {order?.order_number && (
                <div className="flex items-center justify-between">
                  <span className="text-white/50 text-sm">Order ID</span>
                  <span className="text-[#ffd700] text-sm font-bold">#{order?.order_number}</span>
                </div>
              )}
              {order?.pickup_address && (
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-400 mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="text-white/40 text-xs">Pickup</p>
                    <p className="text-white text-sm font-medium">{order?.pickup_address}</p>
                  </div>
                </div>
              )}
              {order?.dropoff_address && (
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#ffd700] mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="text-white/40 text-xs">Dropoff</p>
                    <p className="text-white text-sm font-medium">{order?.dropoff_address}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between pt-1 border-t border-white/5">
                {order?.total_amount && (
                  <span className="text-white font-bold text-sm">KSh {order?.total_amount?.toLocaleString()}</span>
                )}
                {order?.created_at && (
                  <span className="text-white/40 text-xs">{formatDate(order?.created_at)}</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Star Rating */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 text-center">
          <p className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-4">
            {reviewerRole === 'driver' ?'Rate this customer'
              : `How was your experience${driverName ? ` with ${driverName}` : ''}?`}
          </p>
          <div className="flex items-center justify-center gap-3 mb-3">
            {[1, 2, 3, 4, 5]?.map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="transition-transform active:scale-90 hover:scale-110"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="w-12 h-12 transition-colors duration-150"
                  fill={(hoverRating || rating) >= star ? '#ffd700' : 'none'}
                  stroke={(hoverRating || rating) >= star ? '#ffd700' : '#ffffff30'}
                  strokeWidth="1.5"
                >
                  <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                </svg>
              </button>
            ))}
          </div>
          <p className="text-[#ffd700] text-base font-bold h-6">
            {starLabels?.[hoverRating || rating] || ''}
          </p>
        </div>

        {/* Quick Tags */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
          <p className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-3">
            Quick Tags <span className="text-white/25 normal-case font-normal">(optional)</span>
          </p>
          <div className="flex flex-wrap gap-2">
            {quickTags?.map(({ key, label, icon }) => (
              <button
                key={key}
                onClick={() => handleTagToggle(key)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-all active:scale-95 ${
                  selectedTags?.includes(key)
                    ? 'bg-[#ffd700] text-[#0a1628]'
                    : 'bg-white/8 text-white/70 border border-white/10 hover:border-[#ffd700]/30 hover:bg-white/12'
                }`}
              >
                <span>{icon}</span>
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Review Text */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
          <p className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-3">
            Review <span className="text-white/25 normal-case font-normal">(optional)</span>
          </p>
          <textarea
            value={reviewText}
            onChange={(e) => setReviewText(e?.target?.value)}
            placeholder="Share your experience..."
            maxLength={300}
            rows={3}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#ffd700]/40 resize-none transition-colors"
          />
          <p className="text-white/20 text-xs text-right mt-1">{reviewText?.length}/300</p>
        </div>

        {/* Photo Upload */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
          <p className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-3">
            Add Photo <span className="text-white/25 normal-case font-normal">(optional)</span>
          </p>
          {photoPreview ? (
            <div className="relative rounded-xl overflow-hidden border border-white/10">
              <img src={photoPreview} alt="Selected photo preview for rating submission" className="w-full h-36 object-cover" />
              <button
                onClick={handleRemovePhoto}
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" className="w-3.5 h-3.5">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <button
              onClick={() => fileInputRef?.current?.click()}
              className="w-full h-20 rounded-xl border-2 border-dashed border-white/15 flex flex-col items-center justify-center gap-1.5 hover:border-[#ffd700]/40 hover:bg-[#ffd700]/5 transition-all"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="#ffffff50" strokeWidth="1.5" className="w-6 h-6">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
              </svg>
              <span className="text-white/30 text-xs">Tap to upload photo</span>
            </button>
          )}
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoSelect} />
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2.5">
            <svg viewBox="0 0 24 24" fill="#ef4444" className="w-4 h-4 flex-shrink-0">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={submitting || rating === 0}
          className="w-full py-4 rounded-2xl font-black text-base transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            background: rating > 0 ? '#ffd700' : '#ffffff15',
            color: rating > 0 ? '#0a1628' : '#ffffff40',
          }}
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="30 70" />
              </svg>
              Submitting...
            </span>
          ) : (
            'Submit Rating'
          )}
        </button>

        <button
          onClick={() => reviewerRole === 'driver' ? navigate('/driver-dashboard-screen') : navigate('/customer-booking-screen')}
          className="w-full py-3 text-white/30 text-sm hover:text-white/50 transition-colors"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
};

export default RatingSubmissionScreen;
